import type { CSSProperties } from 'react'
import type { UnifiedSelectionResizeHandle } from '@/lib/transforms/types/unified-selection-resize-handle'
import {
  HANDLE_OFFSET,
  HANDLE_SIZE,
  MIN_CHROME_SIZE_PX,
} from '@/components/app/editor/selection-chrome/selection-chrome-constants'

/**
 * Below this SCREEN-px axis length, eight fixed-screen-size handles cannot sit at normal
 * edge/corner positions without overlapping. Handles no longer scale with zoom, so this is
 * compared against the frame's rendered (screen) size, not its raw canvas/doc size.
 */
export const MIN_FRAME_AXIS_FOR_SPREAD_HANDLES = HANDLE_SIZE + HANDLE_OFFSET * 2

/** Screen px step between co-located handles so each anchor stays individually clickable. */
export const STACKED_RESIZE_HANDLE_STEP_PX = 2

/** Stacked layout keeps handles in three bands (top / sides / bottom) with slight in-band offsets. */
const STACKED_RESIZE_HANDLE_GROUPS: UnifiedSelectionResizeHandle[][] = [
  ['nw', 'n', 'ne'],
  ['w', 'e'],
  ['sw', 's', 'se'],
]

/** `scale` is the current canvas zoom — handles are fixed screen size, so overlap depends on the frame's rendered (screen) size, not its doc-px size. */
export function selectionResizeHandlesShouldStack(
  frameWidth: number,
  frameHeight: number,
  scale: number
): boolean {
  if (!Number.isFinite(frameWidth) || !Number.isFinite(frameHeight) || !Number.isFinite(scale)) {
    return false
  }

  const screenWidth = frameWidth * scale
  const screenHeight = frameHeight * scale

  return (
    screenWidth < MIN_FRAME_AXIS_FOR_SPREAD_HANDLES ||
    screenHeight < MIN_FRAME_AXIS_FOR_SPREAD_HANDLES
  )
}

/**
 * Anchor point (frame-local doc px) each handle centers on. Handles are rendered at a fixed
 * screen size via `translate(-50%, -50%) scale(var(--chrome-inv-scale))` in `ResizeHandles`, so
 * these are exact edge/corner/midpoint coordinates — no inset offset needed.
 */
function stackedResizeHandleAnchor(
  key: UnifiedSelectionResizeHandle,
  w: number,
  h: number
): CSSProperties {
  const halfW = w / 2
  const halfH = h / 2
  const step = STACKED_RESIZE_HANDLE_STEP_PX

  switch (key) {
    case 'nw':
      return { left: halfW - step, top: 0 }
    case 'n':
      return { left: halfW, top: 0 }
    case 'ne':
      return { left: halfW + step, top: 0 }
    case 'w':
      return { left: 0, top: halfH }
    case 'e':
      return { left: w, top: halfH }
    case 'sw':
      return { left: halfW - step, top: h }
    case 's':
      return { left: halfW, top: h }
    case 'se':
      return { left: halfW + step, top: h }
    default:
      return {}
  }
}

export function getResizeHandleStackZIndex(
  key: UnifiedSelectionResizeHandle,
  frameWidth: number,
  frameHeight: number,
  scale: number,
  options?: { forceStacked?: boolean }
): number {
  const stacked =
    options?.forceStacked ?? selectionResizeHandlesShouldStack(frameWidth, frameHeight, scale)

  if (!stacked) {
    return 2
  }

  for (let groupIndex = 0; groupIndex < STACKED_RESIZE_HANDLE_GROUPS.length; groupIndex++) {
    const indexInGroup = STACKED_RESIZE_HANDLE_GROUPS[groupIndex].indexOf(key)

    if (indexInGroup >= 0) {
      return 10 + groupIndex * 10 + indexInGroup
    }
  }

  return 2
}

/**
 * Anchor point (frame-local doc px) for a handle — the exact edge/corner/midpoint of the frame
 * it centers on. The caller applies `translate(-50%, -50%) scale(var(--chrome-inv-scale))` so the
 * handle renders at a fixed screen size regardless of canvas zoom.
 */
export function getResizeHandleBoxPositionStyle(
  key: UnifiedSelectionResizeHandle,
  w: number,
  h: number,
  scale: number,
  options?: { forceStacked?: boolean }
): CSSProperties {
  const stacked = options?.forceStacked ?? selectionResizeHandlesShouldStack(w, h, scale)

  if (stacked) {
    return stackedResizeHandleAnchor(key, w, h)
  }

  const halfW = w / 2
  const halfH = h / 2

  switch (key) {
    case 'nw':
      return { left: 0, top: 0 }
    case 'ne':
      return { left: w, top: 0 }
    case 'sw':
      return { left: 0, top: h }
    case 'se':
      return { left: w, top: h }
    case 'n':
      return { left: halfW, top: 0 }
    case 's':
      return { left: halfW, top: h }
    case 'w':
      return { left: 0, top: halfH }
    case 'e':
      return { left: w, top: halfH }
    default:
      return {}
  }
}

/**
 * Expand the selection chrome bounding box so each axis is at least MIN_CHROME_SIZE_PX
 * screen pixels wide, centered on the object's true midpoint.
 *
 * Only the axis that is too narrow grows; a wide axis is left untouched. The conversion
 * from screen pixels to canvas points uses `scale` (current zoom level).
 */
export function expandedChromeBounds(
  x: number,
  y: number,
  w: number,
  h: number,
  scale: number
): { x: number; y: number; width: number; height: number } {
  const minPt = MIN_CHROME_SIZE_PX / Math.max(scale, 0.0001)
  const expandedW = Math.max(w, minPt)
  const expandedH = Math.max(h, minPt)
  const cx = x + w / 2
  const cy = y + h / 2

  return {
    x: cx - expandedW / 2,
    y: cy - expandedH / 2,
    width: expandedW,
    height: expandedH,
  }
}

export const RESIZE_HANDLE_KEYS: UnifiedSelectionResizeHandle[] = [
  'nw',
  'ne',
  'sw',
  'se',
  'n',
  's',
  'e',
  'w',
]
